/* eslint-disable react-hooks/exhaustive-deps */
import { scrollbar } from '@eagle/react-common';
import { Stack, styled, Table, TableBody, TableContainer, TableHead } from '@ethings/ui-toolkit';
import { Box, Theme, useTheme } from '@mui/material';
import { unstable_styleFunctionSx as unstableStyleFunctionSx } from '@mui/system';
import { FC, ReactNode, RefObject, UIEvent, useEffect, useRef, type JSX } from 'react';
import { scrollIntoView } from 'seamless-scroll-polyfill';
import { TimelineTooltip } from './timeline-tooltip';
import { TimelineTooltipBar } from './timeline-tooltip-bar';
import { tableStyles } from './video-segment.styles';

interface Props {
  alertRow: JSX.Element;
  areaRef: RefObject<HTMLDivElement | null>;
  enableTooltip: boolean;
  features: {
    featureDisplay: string;
    renderRow: JSX.Element;
  }[];
  featureRows: ReactNode;
  finishDate: string;
  handleGridLoaded: () => void;
  handleLineOffset: (value: number) => void;
  handleMouseLeave: () => void;
  handleScroll: (event: UIEvent) => void;
  lineOffset: number;
  scrollOptions?: {
    start: boolean;
    end: boolean;
  };
  tableContainerRef: RefObject<HTMLTableElement | null>;
  tableHeadRow: JSX.Element;
  tooltipTime: string;
}

interface StyledTableContainerProps {
  appTheme: Theme;
}

const StyledTableContainer = styled(TableContainer, {
  shouldForwardProp: (prop) => prop !== 'appTheme',
})<StyledTableContainerProps>(({ appTheme }) => {
  const scrollbarCss = unstableStyleFunctionSx({ sx: scrollbar, theme: appTheme } as never) as Record<string, unknown>;
  const webkitScrollbarCss = (scrollbarCss['&::-webkit-scrollbar'] ?? {}) as Record<string, unknown>;

  return {
    ...scrollbarCss,
    border: appTheme.videoSegment.tableBorder,
    display: 'flex',
    flexDirection: 'row',
    '&::-webkit-scrollbar': {
      ...webkitScrollbarCss,
      height: appTheme.videoSegment.scrollbarHeight,
    },
  };
});

const StyledTableBody = styled(TableBody)({
  flexGrow: 1,
});

export const LayoutDesktop: FC<Props> = ({
  alertRow,
  areaRef,
  enableTooltip,
  featureRows,
  features,
  finishDate,
  handleGridLoaded,
  handleLineOffset,
  handleMouseLeave,
  handleScroll,
  lineOffset,
  scrollOptions,
  tableContainerRef,
  tableHeadRow,
  tooltipTime,
}) => {
  const { classes } = tableStyles();
  const theme = useTheme();

  const startScroll = useRef<HTMLDivElement>(null);
  const endScroll = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!scrollOptions) return;
    scrollOptions.start && scrollIntoView(startScroll.current as Element, { behavior: 'smooth' });
    scrollOptions.end && scrollIntoView(endScroll.current as Element, { behavior: 'smooth' });
  }, [scrollOptions]);

  useEffect(() => {
    handleGridLoaded();
  }, [features]);

  useEffect(() => {
    const timeComp = document.getElementsByClassName(finishDate);
    if (timeComp.length === 0) return;
    scrollIntoView(timeComp[0], { behavior: 'smooth', inline: 'center' });
  }, [finishDate]);

  return (
    <Stack ref={areaRef} direction="row" flexGrow={1} overflow="auto">
      <TimelineTooltipBar enableTooltip={enableTooltip} left={lineOffset} />
      <StyledTableContainer
        ref={tableContainerRef}
        onScroll={handleScroll}
        appTheme={theme}
      >
        <Box ref={startScroll} />
        <Table aria-label="video-table" className={classes.table}>
          <TableHead>
            {tableHeadRow}
          </TableHead>
          <TimelineTooltip
            areaRef={areaRef}
            enableTooltip={enableTooltip}
            lineOffset={lineOffset}
            tooltipTime={tooltipTime}
          >
            <StyledTableBody
              data-testid='camera-content-timeline'
              onMouseLeave={handleMouseLeave}
              onMouseMove={(event) => handleLineOffset(event.clientX)}
            >
              {alertRow}
              {featureRows}
            </StyledTableBody>
          </TimelineTooltip>
        </Table>
        <Box ref={endScroll} />
      </StyledTableContainer>
    </Stack>
  );
};
